
import {loadJson} from "../network/fetcher.js";
import * as topojson from "topojson-client";
import {report} from "../report.js";
import {$} from "../util/seq.js";
import {uniq} from "../util/arrays.js";
import {mapObject} from "../lib/underscore.js";
import {makeDynamicRenderer, makeFillStrokeRenderer} from "../canvas/twod.js";

const NORMAL_LINE = 1.25;
const LIGHT_LINE = 1;

// CONSIDER: this is very similar to GeoJSON annotation styling (see annotator.js), so treat the map geojson objects
//           the same way, and just have two rendering options: canvas and svg (or even webgl?)
// CONSIDER: make these lines brighter/more opaque when viewing ocean layers
const coastlineStyle = {strokeStyle: "rgba(250,250,250,0.65)", lineWidth: NORMAL_LINE};
const lakeStyle =      {strokeStyle: "rgba(250,250,250,0.65)", lineWidth: NORMAL_LINE};
const riverStyle =     {strokeStyle: "rgba(220,220,220,0.30)", lineWidth: NORMAL_LINE};
const admin0Style =    {strokeStyle: "rgba(250,250,250,0.45)", lineWidth: NORMAL_LINE};
const pacificStyle =   {strokeStyle: "rgba(250,250,250,0.30)", lineWidth: NORMAL_LINE, lineDash: [5,5]};
const disputedStyle =  {strokeStyle: "rgba(250,250,250,0.45)", lineWidth: NORMAL_LINE, lineDash: [3,3]};
const admin1Style =    {strokeStyle: "rgba(250,250,250,0.45)", lineWidth: LIGHT_LINE,  lineDash: [2,2]};

export const mapElementsV2 = {
    admin_1: {
        admin_1_110m: {file: "/map/v2/admin_1_110m-topo.json", style: admin1Style, scene: "lo"},
        admin_1_50m:  {file: "/map/v2/admin_1_50m-topo.json",  style: admin1Style, scene: "hi"},
    },
    disputed_areas: {
        disputed_50m: {file: "/map/v2/disputed_50m-topo.json", style: disputedStyle, scene: "hi"},
    },
    pacific_groupings: {
        pacific_110m: {file: "/map/v2/pacific_110m-topo.json", style: pacificStyle, scene: "lo"},
        pacific_50m:  {file: "/map/v2/pacific_50m-topo.json",  style: pacificStyle, scene: "hi"},
    },
    admin_0: {
        admin_0_110m: {file: "/map/v2/admin_0_110m-topo.json", style: admin0Style, scene: "lo"},
        admin_0_50m:  {file: "/map/v2/admin_0_50m-topo.json",  style: admin0Style, scene: "hi"},
    },
    rivers: {
        rivers_110m: {file: "/earth-topo.json", style: riverStyle, scene: "lo"},
        rivers_50m:  {file: "/earth-topo.json", style: riverStyle, scene: "hi"},
    },
    lakes: {
        lakes_110m: {file: "/earth-topo.json", style: lakeStyle, scene: "lo"},
        lakes_50m:  {file: "/earth-topo.json", style: lakeStyle, scene: "hi"},
    },
    coastline: {
        coastline_110m: {file: "/earth-topo.json", style: coastlineStyle, scene: "lo"},
        coastline_50m:  {file: "/earth-topo.json", style: coastlineStyle, scene: "hi"},
    },
};

async function download(mapElements) {
    const files = uniq(Object.values(mapElements).map(e => e.file));
    const downloads = await Promise.all(files.map(loadJson));
    return mapObject(mapElements, e => downloads[files.indexOf(e.file)]);
}

/**
 * @param map_scene
 * @param mapLo
 * @param mapHi
 * @param cancel
 * @returns {Promise} a promise for the downloaded and decoded map scene
 */
async function buildMap(map_scene, mapLo, mapHi, cancel) {
    report.status("Downloading...");
    try {
        console.time("build map");

        const activeElements = {};
        map_scene.elements.forEach(e => Object.assign(activeElements, mapElementsV2[e]));
        const downloads = await download(activeElements);
        if (cancel.requested) {
            return;
        }
        const meshes = mapObject(downloads, topojson.feature);

        function buildRenderer(scene) {
            return Object.keys(activeElements)
                .filter(element => activeElements[element].scene === scene)
                .map(element => makeFillStrokeRenderer(meshes[element], activeElements[element].style));
        }

        mapLo.clear();
        buildRenderer("lo").forEach(r => mapLo.add(r));
        mapHi.clear();
        buildRenderer("hi").forEach(r => mapHi.add(r));

    } finally {
        console.timeEnd("build map");
    }
}

/**
 * @param agents
 * @param {Handle} map_scene
 */
export function defineMapBuilder(agents, map_scene) {
    const {mapAgent} = agents;

    const mapLo = makeDynamicRenderer();
    const mapHi = makeDynamicRenderer();

    function build() {
        mapAgent.submit(async function() {
            await buildMap(map_scene.get(), mapLo, mapHi, this.cancel);
            return {mapLo, mapHi};
        });
    }

    map_scene.on($`change`, build);
}
